import { isVolumeManaged } from '../classification';
import {
  signalColorTokens,
  statusStyleFor,
  isSignalStatus,
  isActivationStatus,
} from '../sidebar/signalColors';
import { infraBubbleStyles as s } from '../../components/BubbleCanvas/bubbleDimColors';
import { volumeBubbleStyleTokens as v } from './volumeBubbleStyles';
import type { PackNode, PackNodeData } from './packNodeTypes';
import type { ExplorationVolume, ViewMode } from '../../types/exploration';

/** Node data for pack layout sizing (accepts full volume objects from hierarchy). */
export interface BubbleDatumNode extends PackNodeData {
  volume?: ExplorationVolume | PackNodeData['volume'];
  size?: number;
}

export const MIN_VOLUME_RENDER_GIB = 1;

/** Pack layout area for a leaf node; volume bubbles floor at 1 GiB. */
export function packValueForNode(data: BubbleDatumNode | null | undefined): number {
  if (!data) return 1;
  if (data.type === 'volume') {
    const volSize =
      data.volume && 'size' in data.volume
        ? (data.volume as ExplorationVolume).size
        : undefined;
    return Math.max(volSize ?? data.value ?? 0, MIN_VOLUME_RENDER_GIB);
  }
  const raw = data.value ?? data.size ?? 1;
  return raw > 0 ? raw : 1;
}

export interface OutsideChipLayoutInput {
  bubbleScreenDiam: number;
  naturalFullChipW: number;
  minChipW: number;
  iconOnlyW: number;
  fullChipW: number;
  maxWidth: number | null;
  text: string;
  charW: number;
  minTextChars: number;
  hasSvgIcon: boolean;
  padding?: number;
  measureWidth?: (candidate: string) => number;
}

export interface OutsideChipLayoutResult {
  show: boolean;
  chipW: number;
  displayText: string;
}

/** Truncate label text to fit a chip's text budget (char-width estimate + ellipsis). */
export function truncateChipTextToBudget(
  text: string,
  textBudget: number,
  charW: number,
  minTextChars: number,
  measureWidth?: (candidate: string) => number
): string {
  if (!text || textBudget <= 0) return '';

  if (measureWidth) {
    if (measureWidth(text) <= textBudget) return text;
    // Binary search over candidate length: each measureWidth call is a real DOM read
    // (getBBox()), not a cheap canvas call, and this runs on every chip on every zoom/pan
    // tick — width is monotonic in length here (more original characters before the
    // ellipsis only ever widens the candidate), so this finds the same longest-fitting
    // candidate a linear scan would, in O(log n) calls instead of O(n).
    let lo = minTextChars;
    let hi = text.length - 1;
    let bestLen = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const candidate = text.slice(0, mid - 1) + '…';
      if (measureWidth(candidate) <= textBudget) {
        bestLen = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return bestLen === -1 ? '' : text.slice(0, bestLen - 1) + '…';
  }

  if (textBudget < charW * minTextChars) return '';
  const maxChars = Math.max(minTextChars, Math.floor(textBudget / charW));
  if (text.length <= maxChars) return text;
  return maxChars > 1 ? text.slice(0, maxChars - 1) + '…' : '';
}

function applyMaxWidthChipTruncation(
  chipW: number,
  displayText: string,
  input: Pick<
    OutsideChipLayoutInput,
    'maxWidth' | 'naturalFullChipW' | 'minChipW' | 'charW' | 'minTextChars' | 'measureWidth'
  >
): { chipW: number; displayText: string } {
  if (!input.maxWidth || input.naturalFullChipW <= input.maxWidth) {
    return { chipW, displayText };
  }
  const cappedW = input.maxWidth;
  const textBudget = cappedW - input.minChipW;
  return {
    chipW: cappedW,
    displayText: truncateChipTextToBudget(
      displayText,
      textBudget,
      input.charW,
      input.minTextChars,
      input.measureWidth
    ),
  };
}

/** Truncate → icon-only → drop for rim chips with optional SVG icons. */
export function resolveOutsideChipLayout(input: OutsideChipLayoutInput): OutsideChipLayoutResult {
  const pad = input.padding ?? 4;
  let chipW = input.fullChipW;
  let displayText = input.text;

  const truncateToCurrentChip = () => {
    const textBudget = chipW - input.minChipW;
    displayText = truncateChipTextToBudget(
      displayText,
      textBudget,
      input.charW,
      input.minTextChars,
      input.measureWidth
    );
  };

  const applyMaxWidthCap = () => {
    const capped = applyMaxWidthChipTruncation(chipW, displayText, input);
    chipW = capped.chipW;
    displayText = capped.displayText;
  };

  const fallbackToIconOnlyIfEmpty = () => {
    if (!displayText) {
      chipW = input.iconOnlyW;
    }
  };

  if (input.hasSvgIcon) {
    const fullThreshold = input.naturalFullChipW + pad;
    const iconThreshold = input.iconOnlyW + pad;
    const truncateThreshold = input.minChipW + input.charW * input.minTextChars + pad;

    if (input.bubbleScreenDiam >= fullThreshold) {
      applyMaxWidthCap();
      fallbackToIconOnlyIfEmpty();
    } else if (input.bubbleScreenDiam >= truncateThreshold) {
      chipW = Math.min(input.maxWidth ?? input.fullChipW, input.bubbleScreenDiam - pad);
      truncateToCurrentChip();
      fallbackToIconOnlyIfEmpty();
    } else if (input.bubbleScreenDiam >= iconThreshold) {
      displayText = '';
      chipW = input.iconOnlyW;
    } else {
      return { show: false, chipW: 0, displayText: '' };
    }
  } else {
    // Category-leaf name chips (no svg icon): visibility is gated by minR in
    // renderChipContent; here we only cap/truncate to maxWidth.
    applyMaxWidthCap();
    if (!displayText) {
      // No icon to fall back to — an empty box is worse than no chip at all.
      return { show: false, chipW: 0, displayText: '' };
    }
  }

  if (displayText && input.measureWidth) {
    chipW = input.minChipW + input.measureWidth(displayText);
  }

  return { show: true, chipW, displayText };
}

/** One row of a status/reason center label (name / size / count). */
export interface InsideCenterLabelLine {
  text: string;
  fontSize: number;
  fontWeight: number;
  fg: string;
}

export interface InsideCenterLabelLayoutInput {
  lines: InsideCenterLabelLine[];
  bubbleScreenDiam: number;
  measureWidth: (text: string, fontSize: number, fontWeight: number) => number;
  minTextChars: number;
  padding?: number;
  lineGap?: number;
}

export interface InsideCenterLabelLayoutResult {
  show: boolean;
  visibleLines: InsideCenterLabelLine[];
}

/**
 * Progressive trailing-row drop + per-line ellipsis for status/reason center labels.
 * Tries full → drop count → drop size → name-only; then size-only; else hide.
 */
export function resolveInsideCenterLabelLayout(
  input: InsideCenterLabelLayoutInput
): InsideCenterLabelLayoutResult {
  const { lines, bubbleScreenDiam, measureWidth, minTextChars } = input;
  const pad = input.padding ?? 4;
  const gap = input.lineGap ?? 2;
  const heightSlack = 4;
  const textBudget = Math.max(0, bubbleScreenDiam - pad);

  if (!lines.length || bubbleScreenDiam <= 0) {
    return { show: false, visibleLines: [] };
  }

  const stackHeight = (subset: InsideCenterLabelLine[]) => {
    if (!subset.length) return 0;
    return (
      subset.reduce((sum, l) => sum + l.fontSize * 1.15, 0) + gap * (subset.length - 1)
    );
  };

  const truncateSubset = (subset: InsideCenterLabelLine[]): InsideCenterLabelLine[] | null => {
    const out: InsideCenterLabelLine[] = [];
    for (const line of subset) {
      const truncated = truncateChipTextToBudget(
        line.text,
        textBudget,
        line.fontSize * 0.56,
        minTextChars,
        (candidate) => measureWidth(candidate, line.fontSize, line.fontWeight)
      );
      if (!truncated) return null;
      out.push({ ...line, text: truncated });
    }
    return out;
  };

  const trySubset = (subset: InsideCenterLabelLine[]): InsideCenterLabelLayoutResult | null => {
    if (!subset.length) return null;
    if (stackHeight(subset) + heightSlack > bubbleScreenDiam) return null;
    const visible = truncateSubset(subset);
    if (!visible) return null;
    return { show: true, visibleLines: visible };
  };

  for (let len = lines.length; len >= 1; len--) {
    const result = trySubset(lines.slice(0, len));
    if (result) return result;
  }

  if (lines.length > 1) {
    const sizeOnly = trySubset([lines[1]!]);
    if (sizeOnly) return sizeOnly;
  }

  return { show: false, visibleLines: [] };
}

/** Minimal pack/hierarchy node shape for bubble fill/stroke helpers. */
export type BubbleDatum = Pick<PackNode, 'data' | 'depth' | 'parent' | 'children'>;

/** Mirrors renderBubbleView center-label skip — container mode when children are visible. */
function statusBubbleHasVisibleChildren(d: BubbleDatum): boolean {
  if (!d.children?.length) return false;
  return d.children.some(
    (c) => c.data?.type === 'volume' || c.data?.type === 'status' || c.data?.type === 'reason'
  );
}

/** Dedicated fill/stroke for a status key's "wraps visible nested bubbles" state, if defined. */
function withChildrenStyleFor(
  key: string | undefined | null
): { fill: string; stroke: string } | undefined {
  if (!key) return undefined;
  return v.status.withChildrenByKey[key as keyof typeof v.status.withChildrenByKey];
}

/** Status/reason leaf drilled to volume children — uses outside name/size chips, not center label. */
export function isCategoryLeafWithVolumeChildren(d: BubbleDatum): boolean {
  if (d.data.type !== 'status' && d.data.type !== 'reason') return false;
  if (!d.children?.length) return false;
  if (d.children.some((c) => c.data?.type === 'status' || c.data?.type === 'reason')) return false;
  return d.children.some((c) => c.data?.type === 'volume');
}

/** Outside name/size chips for status/reason containers (volume leaves or nested categories). */
export function usesOutsideStatusChips(d: BubbleDatum): boolean {
  if (d.data.type !== 'status' && d.data.type !== 'reason') return false;
  return statusBubbleHasVisibleChildren(d);
}

export { isVolumeManaged };

/** True when this node is the only visible top-level group (drilled-in root). */
export function isSoleVisibleRoot(d: BubbleDatum): boolean {
  return d.depth === 1 && (d.parent?.children?.length ?? 0) === 1;
}

export function isUnattachedVolume(
  volume: Pick<ExplorationVolume, 'instanceId'> | null | undefined
): boolean {
  return !!volume && !volume.instanceId;
}

/**
 * Inner/outer radius ratio for managed (autoscaling) volume discs.
 * Outer pack radius uses original `size`; inner uses √(current/original) so area
 * tracks `datafyDiskSize`. When current is missing or equals original, ratio is
 * ~0.95 (small ring) — rings look almost identical by design.
 */
export function managedInnerRatio(vol: Pick<ExplorationVolume, 'size' | 'datafyDiskSize'>): number {
  const original = vol.size || 0;
  const current = vol.datafyDiskSize || original;
  if (!original) return 0;
  return Math.min(0.95, Math.sqrt(current / original));
}

export const HIERARCHY_TYPES = new Set([
  'account',
  'cluster',
  'nodeGroup',
  'instance',
  'ec2',
  'unattached',
  'region',
  'tag',
  'status',
]);

/** Per-type halo / hover-rim colors. */
export const hierarchyRings: Record<string, { ringFill: string; ringStroke: string }> = {
  account: { ringFill: '#EDF1FE', ringStroke: signalColorTokens.borders.hierarchy },
  cluster: { ringFill: 'var(--color-magnolia)', ringStroke: signalColorTokens.borders.hierarchy },
  nodeGroup: { ringFill: 'var(--color-pale-yellow)', ringStroke: signalColorTokens.borders.hierarchy },
  instance: { ringFill: '#F7F9FF', ringStroke: signalColorTokens.borders.hierarchy },
  ec2: { ringFill: '#F7F9FF', ringStroke: signalColorTokens.borders.hierarchy },
  region: { ringFill: 'var(--color-primary-70)', ringStroke: signalColorTokens.borders.hierarchy },
  tag: { ringFill: 'var(--color-light-green)', ringStroke: signalColorTokens.borders.hierarchy },
  unattached: {
    ringFill: signalColorTokens.unattachedFill,
    ringStroke: signalColorTokens.borders.hierarchy,
  },
};

/** Hover-rim / halo stroke for hierarchy bubbles (primary-500). */
export const hierarchyRingHighStroke = signalColorTokens.borders.hierarchyHigh;

export function fillForBubble(d: BubbleDatum) {
  const data = d.data;
  if (data._isOverflow) return v.overflow.fill;
  if (data.type === 'volume') {
    if (isVolumeManaged(data.volume)) return v.volume.managed.outerFill;
    if (isUnattachedVolume(data.volume)) return v.volume.unattached.fill;
    return v.volume.default.fill;
  }
  if (data.type === 'status' || data.type === 'reason') {
    if (statusBubbleHasVisibleChildren(d)) {
      const override = withChildrenStyleFor(data.key);
      if (override) return override.fill;
      if (isSignalStatus(data.key)) return v.status.withChildrenFill;
    }
    return statusStyleFor(data.key).fill;
  }
  if (data.type === 'unattached') {
    return isSoleVisibleRoot(d) ? v.volume.unattached.fill : 'var(--color-white)';
  }
  if (HIERARCHY_TYPES.has(data.type)) return 'var(--color-white)';
  return 'var(--color-gray-200)';
}

export function strokeForBubble(d: BubbleDatum, _view: ViewMode) {
  const data = d.data;
  if (data._isOverflow) return v.overflow.stroke;
  if (data.type === 'volume') {
    if (isVolumeManaged(data.volume)) return v.volume.managed.outerStroke;
    return v.volume.default.stroke;
  }
  if (data.type === 'status' || data.type === 'reason') {
    if (statusBubbleHasVisibleChildren(d)) {
      const override = withChildrenStyleFor(data.key);
      if (override) return override.stroke;
    }
    return statusStyleFor(data.key).stroke;
  }
  if (HIERARCHY_TYPES.has(data.type)) {
    return data.type === 'status' ? signalColorTokens.borders.hierarchy : s.bubble.border.default;
  }
  return 'var(--color-gray-400)';
}

export function fillOpacityForBubble(d: BubbleDatum) {
  if (d.data.type === 'volume') return v.volume.default.fillOpacity;
  return 1;
}

/** Screen-pixel bubble outline width (`vector-effect: non-scaling-stroke`). */
export const BUBBLE_STROKE_WIDTH = 1;

export function strokeWidthForBubble(_d: BubbleDatum) {
  return BUBBLE_STROKE_WIDTH;
}

export function strokeOpacityForBubble(d: BubbleDatum) {
  if (d.data.type === 'status' || d.data.type === 'reason') {
    return signalColorTokens.mutedStrokeKeys.has(d.data.key ?? '') ? v.status.mutedStrokeOpacity : 1;
  }
  return 1;
}

export function strokeDashForBubble(d: BubbleDatum) {
  if (d.data.type === 'volume' && isVolumeManaged(d.data.volume)) return v.volume.managed.dash;
  return null;
}

export { isSignalStatus, isActivationStatus };
